import { ACTION_LOG_STATUS } from '@/@core/data/enum/actionLogStatus'
import flatPickr from 'vue-flatpickr-component'
import dayjs from 'dayjs'
import { getSystemLog, getActionMenu } from '@/api/action-defines/systemLog'
import { mapGetters } from 'vuex'
import { getInfoUser } from '@/api/action-defines/site-management'
import LogTable from '@/views/modules/action-log/LogTable/LogTable.vue'

export default {
  name: 'ActionLog',
  components: {
    flatPickr,
    LogTable,
  },
  data() {
    return {
      logStatus: { ...ACTION_LOG_STATUS },
      buttonDisabled: 'expand',
      dateRangeConfig: {
        mode: 'range',
        locale: { rangeSeparator: ' -> ' },
        enableTime: true,
        time_24hr: true,
        dateFormat: 'd/m/Y H:i',
      },
      dateRange: null,
      searchData: {
        FromDate: null,
        ToDate: null,
        Action: [],
        CreatedBy: [],
      },
      // Mỗi nhóm chức năng là một tab của bộ lọc; dữ liệu do backend trả về nên không còn bảng ánh xạ cứng.
      actionGroups: [],
      actionMenus: [],
      listCreatedBy: [],
      logData: [],
      // Pagination
      paging: {
        PageSize: 20,
        PageIndex: 1,
      },
      totalPagination: {
        TotalRecord: 0,
        TotalPage: 0,
      },
    }
  },

  async created() {
    await this.load()
    await this.getActionMenuLoad()

    // Mặc định là những gì ĐÃ xảy ra trong ngày: từ đầu ngày đến thời điểm mở màn hình.
    // (Trước đây lấy từ hiện tại đến hết ngày — tức là khoảng thời gian chưa xảy ra, luôn rỗng.)
    const startOfToday = dayjs().startOf('day')

    // Chốt mốc cuối ở cuối PHÚT hiện tại: ô chọn ngày chỉ hiển thị tới phút, nếu lấy đúng giây hiện tại
    // thì thao tác vừa làm cách đây vài giây trong cùng phút sẽ bị loại khỏi kết quả.
    const now = dayjs().endOf('minute')

    this.dateRange = `${startOfToday.format('DD/MM/YYYY HH:mm')} -> ${now.format('DD/MM/YYYY HH:mm')}`
    this.searchData.FromDate = startOfToday.toISOString()
    this.searchData.ToDate = now.toISOString()

    await this.getLogs()
  },

  watch: {
    // Đổi trang phải nạp lại dữ liệu — trước đây chỉ gán paging rồi thôi nên bấm sang trang không có tác dụng.
    paging: {
      deep: true,
      handler() {
        this.getLogs()
      },
    },

    // Tên thao tác, danh mục bộ lọc, tên đối tượng và nhãn trường do backend dịch theo ngôn ngữ gửi lên ⇒ đổi
    // ngôn ngữ phải nạp lại, nếu không màn hình giữ nguyên chữ của ngôn ngữ cũ cho tới khi F5.
    '$i18n.locale'() {
      this.getActionMenuLoad()
      this.getLogs()
    },
  },

  methods: {
    async load() {
      try {
        const res = await getInfoUser()
        if (res.StatusCode === '200') {
          this.listCreatedBy = res.Data.map(user => ({
            value: user.UserId,
            text: user.FullName || user.Email,
          }))
        }
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },

    /**
     * Nạp danh mục chức năng cho bộ lọc.
     * Backend trả sẵn nhãn đã dịch theo ngôn ngữ đang dùng, front-end chỉ đổ vào danh sách.
     */
    async getActionMenuLoad() {
      try {
        const res = await getActionMenu()
        if (res.StatusCode !== '200' || !Array.isArray(res.Data)) return

        this.actionMenus = res.Data.map(group => group.ModuleName || group.Module)
        this.actionGroups = res.Data.map(group => (group.Items || []).map(item => ({
          value: item.Code,
          text: item.Name,
          childs: [],
        })))
      } catch (error) {
        console.error('Error loading action catalog:', error)
      }
    },

    async getLogs() {
      this.setSearchDate()

      const BodyData = {
        ...this.searchData,
        // Mã chức năng là chuỗi, gửi thẳng — không còn bước đổi sang số.
        Action: (this.searchData.Action || []).filter(code => typeof code === 'string' && code),
        ...this.paging,
      }

      await getSystemLog({ BodyData }).then(res => {
        if (res.StatusCode !== '200') return

        const data = res.Data || {}
        this.logData = (data.Items || []).map(log => ({
          ...log,
          isCollapsed: false,
          CreatedDate: dayjs(log.CreatedDate).format('DD/MM/YYYY HH:mm'),
        }))

        this.totalPagination.TotalRecord = data.TotalRecord || 0
        this.totalPagination.TotalPage = data.TotalPage || 0
      })
    },

    changePage(paging) {
      this.paging = paging
    },

    collapseAll() {
      this.logData.forEach(log => this.$set(log, 'isCollapsed', true))
      this.buttonDisabled = 'collapse'
    },

    expandAll() {
      this.logData.forEach(log => this.$set(log, 'isCollapsed', false))
      this.buttonDisabled = 'expand'
    },

    toggleCollapseLogRow(log) {
      this.$set(log, 'isCollapsed', !log.isCollapsed)
    },

    searchActionLog() {
      // Lọc lại luôn quay về trang đầu, nếu không sẽ hiện trang trống khi kết quả mới ít hơn.
      if (this.paging.PageIndex !== 1) {
        this.paging = { ...this.paging, PageIndex: 1 }
        return
      }
      this.getLogs()
    },

    onCreatedByChange() {
      this.searchActionLog()
    },

    setSearchDate() {
      if (!this.dateRange || typeof this.dateRange !== 'string') return

      const dateRange = this.dateRange.split('->')
      if (!dateRange[0] || !dateRange[1]) return

      this.searchData.FromDate = dayjs(dateRange[0].trim(), 'DD/MM/YYYY HH:mm').toISOString()
      this.searchData.ToDate = dayjs(dateRange[1].trim(), 'DD/MM/YYYY HH:mm').toISOString()
    },
  },

  computed: {
    ...mapGetters({
      $ls_CurrentSiteID: 'auth/currentSiteID',
    }),
  },
}
